import type { InvitePreviewResponse } from '@mm/shared';
import { useEffect, useState, type FormEvent } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { acceptInvite, fetchInvitePreview } from '../api/invites';
import { useAuth } from '../auth/AuthContext';
import { homeForRole } from '../auth/RequireRole';
import { Button } from '../ui/Button';
import { Field } from '../ui/Field';
import { Input } from '../ui/Input';
import { ThemeToggle } from '../ui/ThemeToggle';

type State =
  | { kind: 'loading' }
  | { kind: 'preview'; preview: InvitePreviewResponse }
  | { kind: 'not_found' }
  | { kind: 'network_error' };

export function InvitePage() {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const { user, setSession } = useAuth();

  const [state, setState] = useState<State>({ kind: 'loading' });
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    fetchInvitePreview(token).then((r) => {
      if (cancelled) return;
      if (r.kind === 'ok') setState({ kind: 'preview', preview: r.preview });
      else if (r.kind === 'not_found') setState({ kind: 'not_found' });
      else setState({ kind: 'network_error' });
    });
    return () => {
      cancelled = true;
    };
  }, [token]);

  // Если пользователь уже вошёл (любая роль) — отправим его на его home.
  // Инвайт открывать смысла нет.
  if (user) return <Navigate to={homeForRole(user.role)} replace />;

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!token) return;
    setFormError(null);
    if (password.length < 8) {
      setFormError('Пароль должен быть не меньше 8 символов');
      return;
    }
    if (password !== confirm) {
      setFormError('Пароли не совпадают');
      return;
    }
    setSubmitting(true);
    const r = await acceptInvite(token, password);
    setSubmitting(false);
    if (r.kind === 'ok') {
      setSession({ accessToken: r.accessToken, user: r.user });
      navigate('/app/my', { replace: true });
      return;
    }
    if (r.kind === 'expired') {
      setState({ kind: 'preview', preview: { ...(state as { preview: InvitePreviewResponse }).preview, expired: true } });
      return;
    }
    if (r.kind === 'consumed') {
      setState({ kind: 'preview', preview: { ...(state as { preview: InvitePreviewResponse }).preview, consumed: true } });
      return;
    }
    if (r.kind === 'not_found') {
      setState({ kind: 'not_found' });
      return;
    }
    setFormError(
      r.kind === 'rate_limited'
        ? 'Слишком много попыток, подождите несколько минут'
        : r.kind === 'invalid_body'
          ? 'Проверьте пароль'
          : 'Не удалось связаться с сервером',
    );
  }

  return (
    <Shell>
      {state.kind === 'loading' && <Loading />}
      {state.kind === 'not_found' && <Message title="Ссылка не найдена" body="Проверьте адрес — возможно, вы скопировали его не полностью." />}
      {state.kind === 'network_error' && (
        <Message title="Ошибка сети" body="Не удалось связаться с сервером. Попробуйте перезагрузить страницу." />
      )}
      {state.kind === 'preview' && state.preview.consumed && (
        <Message
          title="Ссылка уже использована"
          body={`Кажется, ${state.preview.studentFullName.split(' ')[0]} уже создал(а) учётку по этой ссылке. Попросите репетитора сгенерировать новую, если нужно восстановить доступ.`}
        />
      )}
      {state.kind === 'preview' && state.preview.expired && !state.preview.consumed && (
        <Message
          title="Ссылка истекла"
          body="Срок действия приглашения вышел. Попросите репетитора прислать новую ссылку."
        />
      )}
      {state.kind === 'preview' && !state.preview.consumed && !state.preview.expired && (
        <PreviewForm
          preview={state.preview}
          password={password}
          confirm={confirm}
          submitting={submitting}
          error={formError}
          onPassword={setPassword}
          onConfirm={setConfirm}
          onSubmit={onSubmit}
        />
      )}
    </Shell>
  );
}

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-screen bg-bg text-fg flex flex-col">
      <header className="flex items-center justify-between px-6 py-5">
        <div className="flex items-baseline gap-2">
          <span
            className="font-display text-[20px] font-semibold"
            style={{ fontVariationSettings: '"opsz" 48, "SOFT" 20' }}
          >
            board
          </span>
          <span className="text-[10px] uppercase tracking-[0.12em] text-muted">matemasha</span>
        </div>
        <ThemeToggle />
      </header>
      <main className="flex-1 flex items-center justify-center px-4 pb-20">
        <div className="w-full max-w-[420px]">{children}</div>
      </main>
      <footer className="px-6 py-4 text-[11px] text-muted font-caps italic">
        board.matemasha · 2026
      </footer>
    </div>
  );
}

function Loading() {
  return <p className="text-muted text-center text-[13px]">Загружаем приглашение…</p>;
}

function Message({ title, body }: { title: string; body: string }) {
  return (
    <div className="text-center">
      <h1
        className="font-display text-[36px] leading-none font-medium mb-3"
        style={{ fontVariationSettings: '"opsz" 144, "SOFT" 20' }}
      >
        {title}
      </h1>
      <p className="text-[13px] text-muted">{body}</p>
    </div>
  );
}

function PreviewForm({
  preview,
  password,
  confirm,
  submitting,
  error,
  onPassword,
  onConfirm,
  onSubmit,
}: {
  preview: InvitePreviewResponse;
  password: string;
  confirm: string;
  submitting: boolean;
  error: string | null;
  onPassword: (v: string) => void;
  onConfirm: (v: string) => void;
  onSubmit: (e: FormEvent) => void;
}) {
  return (
    <>
      <h1
        className="font-display text-[44px] leading-none font-medium mb-2"
        style={{ fontVariationSettings: '"opsz" 144, "SOFT" 20' }}
      >
        Привет, {preview.studentFullName.split(' ')[0]}
      </h1>
      <p className="font-caps italic text-[13px] text-muted mb-8">
        {preview.tutorName} приглашает вас в свой класс. Придумайте пароль для входа.
      </p>

      <form onSubmit={onSubmit} className="space-y-4">
        <Field label="Ваше имя">
          <Input type="text" value={preview.studentFullName} readOnly disabled />
        </Field>
        <Field label="Новый пароль" hint="Не меньше 8 символов">
          <Input
            type="password"
            value={password}
            onChange={(e) => onPassword(e.target.value)}
            required
            minLength={8}
            autoFocus
            autoComplete="new-password"
          />
        </Field>
        <Field label="Повторите пароль">
          <Input
            type="password"
            value={confirm}
            onChange={(e) => onConfirm(e.target.value)}
            required
            minLength={8}
            autoComplete="new-password"
          />
        </Field>
        {error ? (
          <p className="text-[12px] text-accent border border-accent/30 bg-accent-soft px-3 py-2 rounded">
            {error}
          </p>
        ) : null}
        <Button type="submit" disabled={submitting} className="w-full h-9">
          {submitting ? 'Входим…' : 'Войти'}
        </Button>
      </form>
    </>
  );
}
